/**
 * Phase 2B.2 — Stripe Checkout success landing.
 *
 * Stripe redirects here with ?session_id=… after payment. We poll
 * /api/billing/session-status/{id} until status flips to "paid"
 * (or we time out). Once paid, the user record has been extended
 * server-side; we refresh and bounce the agent into /admin.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Home } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30;  // ~60s

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refresh } = useAuth();

  // 'pending' | 'paid' | 'expired' | 'error' | 'missing'
  const [state, setState] = useState(sessionId ? "pending" : "missing");
  const [grantedUntil, setGrantedUntil] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/session-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          setGrantedUntil(data.granted_until);
          // Pull fresh user to update billing badge in nav, etc.
          await refresh?.();
          // Auto-redirect after a beat so agents see the win.
          setTimeout(() => { if (!cancelled) nav("/admin"); }, 2200);
          return;
        }
        if (data.payment_status === "expired") {
          setState("expired");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          setErrMsg("We couldn't confirm your payment in time. Refresh in a minute, or check your email for the receipt.");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setErrMsg(e?.response?.data?.detail || "Couldn't reach the billing service.");
      }
    };

    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId, refresh, nav]);

  return (
    <div data-testid="billing-success-page"
         style={{ minHeight: "100vh", background: "var(--surface)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div className="card" style={{ padding: "44px 48px", maxWidth: 520, width: "100%", textAlign: "center" }}>
        <span style={{
          width: 56, height: 56, borderRadius: 16, margin: "0 auto 22px",
          background: state === "paid" ? "#3F8B5A" : "var(--primary)",
          color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {state === "pending" && <Loader2 size={26} className="spin" />}
          {state === "paid" && <CheckCircle2 size={26} />}
          {(state === "error" || state === "missing" || state === "expired") && <AlertCircle size={26} />}
        </span>

        {state === "pending" && (
          <>
            <h1 className="font-serif" style={{ fontSize: 28, lineHeight: 1.2, margin: 0, marginBottom: 10, fontWeight: 500 }}>
              Confirming your payment…
            </h1>
            <p style={{ color: "var(--text-muted)", margin: 0, lineHeight: 1.55 }}>
              Stripe is processing — this usually takes a few seconds. Don't close this tab.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <h1 className="font-serif" style={{ fontSize: 28, lineHeight: 1.2, margin: 0, marginBottom: 10, fontWeight: 500 }}>
              You're <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>in.</em>
            </h1>
            <p style={{ color: "var(--text-muted)", margin: 0, marginBottom: 20, lineHeight: 1.55 }}>
              Welcome to PorchLight. Taking you to your portal…
            </p>
            {grantedUntil && (
              <p data-testid="billing-success-end" style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
                Next renewal: {new Date(grantedUntil).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
              </p>
            )}
          </>
        )}

        {(state === "error" || state === "expired" || state === "missing") && (
          <>
            <h1 className="font-serif" style={{ fontSize: 26, lineHeight: 1.2, margin: 0, marginBottom: 10, fontWeight: 500 }}>
              {state === "expired" ? "That checkout expired" : "Something didn't go through"}
            </h1>
            <p data-testid="billing-success-error"
               style={{ color: "var(--text-muted)", margin: 0, marginBottom: 20, lineHeight: 1.55 }}>
              {state === "missing"
                ? "We didn't get a session id, so there's nothing to confirm."
                : (errMsg || "Try clicking Subscribe again from your portal.")}
            </p>
            <Link to="/admin" className="btn-primary"
                  style={{ textDecoration: "none", justifyContent: "center" }}>
              <Home size={14} /> Back to portal
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
